<template>
  <div class="brand-market-performance-section shadow-sm mb-4">
    <div class="card border-0 shadow-sm mb-3" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
      <div class="card-body text-white">
        <div class="d-flex align-items-center">
          <i class="fas fa-chart-bar me-2"></i>
          <h5 class="mb-0">{{ $t('dashboard.brandPerformance.title') }}</h5>
        </div>
      </div>
    </div>
    <div class="card-body">
      <p class="text-muted small mb-4">{{ $t('dashboard.brandPerformance.description') }}</p>

      <div class="row">
        <!-- Market Share by Category -->
        <div class="col-md-6 mb-4">
          <div class="card h-100 shadow-sm">
            <div class="card-body">
              <h6 class="card-title"><i class="fas fa-chart-pie me-2 text-primary"></i>{{ $t('dashboard.brandPerformance.marketShareByCategory') }}</h6>
              <hr>
              <div v-if="marketShareData.length > 0">
                <table class="table table-borderless table-sm mb-0">
                  <thead>
                    <tr>
                      <th>{{ $t('dashboard.brandPerformance.category') }}</th>
                      <th class="text-end">{{ $t('dashboard.brandPerformance.adataShare') }}</th>
                      <th class="text-end">{{ $t('dashboard.brandPerformance.trend') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in marketShareData" :key="item.category">
                      <td>
                        <i :class="getCategoryIcon(item.category) + ' me-2'"></i>
                        {{ item.category }}
                      </td>
                      <td class="text-end">
                        <span class="me-2" :style="{ color: getShareColor(item.adata_share) }">●</span>
                        {{ item.adata_share.toFixed(1) }}%
                      </td>
                      <td class="text-end">
                        <span :class="getTrendClass(item.trend)">
                          <i :class="getTrendIcon(item.trend) + ' me-1'"></i>
                          {{ item.trend > 0 ? '+' : '' }}{{ item.trend.toFixed(1) }}%
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center text-muted py-3">
                {{ $t('dashboard.noDataAvailable') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Competitive Positioning -->
        <div class="col-md-6 mb-4">
          <div class="card h-100 shadow-sm">
            <div class="card-body">
              <h6 class="card-title"><i class="fas fa-trophy me-2 text-warning"></i>{{ $t('dashboard.brandPerformance.competitivePositioning') }}</h6>
              <hr>
              <div v-if="competitivePositioningData.length > 0">
                <table class="table table-borderless table-sm mb-0">
                  <thead>
                    <tr>
                      <th>{{ $t('dashboard.brandPerformance.competitor') }}</th>
                      <th class="text-end">{{ $t('dashboard.brandPerformance.marketShare') }}</th>
                      <th class="text-end">{{ $t('dashboard.brandPerformance.vsAdata') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in competitivePositioningData" :key="item.competitor">
                      <td>
                        <span class="me-2" :style="{ color: getCompetitorColor(item.competitor) }">●</span>
                        {{ item.competitor }}
                      </td>
                      <td class="text-end">{{ item.market_share.toFixed(1) }}%</td>
                      <td class="text-end">
                        <span :class="getTrendClass(item.vs_adata)">
                          <i :class="getTrendIcon(item.vs_adata) + ' me-1'"></i>
                          {{ item.vs_adata > 0 ? '+' : '' }}{{ item.vs_adata.toFixed(1) }}%
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center text-muted py-3">
                {{ $t('dashboard.noDataAvailable') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { getBrandMarketPerformanceSummary } from '../api';

export default {
  name: 'BrandMarketPerformanceSummary',
  setup() {
    const marketShareData = ref([]);
    const competitivePositioningData = ref([]);

    const fetchBrandMarketPerformanceSummary = async () => {
      try {
        const data = await getBrandMarketPerformanceSummary();
        marketShareData.value = data.market_share_by_category.data || [];
        competitivePositioningData.value = data.competitive_positioning.data || [];
      } catch (error) {
        console.error('Failed to fetch brand market performance summary:', error);
        marketShareData.value = [];
        competitivePositioningData.value = [];
      }
    };

    const getCategoryIcon = (category) => {
      switch (category) {
        case 'DRAM': return 'fas fa-memory text-info';
        case 'SSD': return 'fas fa-hdd text-primary';
        case 'USB Drive': return 'fas fa-usb text-secondary';
        default: return 'fas fa-microchip text-muted';
      }
    };

    const getShareColor = (share) => {
      if (share > 30) return '#28a745'; // Green
      if (share > 20) return '#17a2b8'; // Cyan
      if (share > 10) return '#ffc107'; // Yellow
      return '#dc3545'; // Red
    };

    const getTrendClass = (trend) => {
      if (trend > 0) return 'text-success';
      if (trend < 0) return 'text-danger';
      return 'text-muted';
    };

    const getTrendIcon = (trend) => {
      if (trend > 0) return 'fas fa-arrow-up';
      if (trend < 0) return 'fas fa-arrow-down';
      return 'fas fa-minus';
    };

    const getCompetitorColor = (competitor) => {
      // Simple color mapping for demonstration
      const colors = {
        'Samsung': '#007bff', // Blue
        'Corsair': '#28a745', // Green
        'Kingston': '#ffc107', // Yellow
        'Crucial': '#17a2b8', // Cyan
        'Western Digital': '#6f42c1', // Purple
        default: '#6c757d' // Gray
      };
      return colors[competitor] || colors.default;
    };

    onMounted(() => {
      fetchBrandMarketPerformanceSummary();
    });

    return {
      marketShareData,
      competitivePositioningData,
      getCategoryIcon,
      getShareColor,
      getTrendClass,
      getTrendIcon,
      getCompetitorColor,
    };
  },
};
</script>

<style scoped>
.brand-market-performance-section .card-header {
  background-color: #6f42c1; /* A shade of purple */
  color: white;
  font-weight: bold;
}

.card-title {
  font-weight: bold;
  color: #333;
}

.table th {
  font-weight: bold;
  color: #555;
}

.table td {
  color: #333;
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-primary {
  color: #007bff !important;
}

.text-secondary {
  color: #6c757d !important;
}

.text-muted {
  color: #6c757d !important;
}
</style>
